import React, { useEffect, useRef, useState } from 'react';
import { DEMO } from './landingCopy';
import { newIdempotencyKey, submitDemoRequest } from './demoRequestApi';
import { contactMailto, getContactEmail } from './landingConfig';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { name: '', organization_name: '', email: '', message: '' };
const LIMITS = { name: 120, organization_name: 160, email: 254, message: 2000 };

const validate = (values) => {
  const errors = {};
  if (!values.name.trim()) errors.name = DEMO.required.name;
  if (!values.organization_name.trim()) errors.organization_name = DEMO.required.organization_name;
  if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = DEMO.required.email;
  return errors;
};

const signature = (values) => JSON.stringify([values.name.trim(), values.organization_name.trim(), values.email.trim(), values.message.trim()]);

const Field = ({ id, label, error, help, children }) => (
  <div className="zl-field">
    <label htmlFor={id}>{label}</label>
    {children}
    {help && <p id={`${id}-help`} className="zl-small">{help}</p>}
    {error && <p id={`${id}-error`} className="zl-field-error">{error}</p>}
  </div>
);

const ContactLink = ({ email, testId = 'demo-contact-link' }) => (
  <a href={contactMailto(email)} className="zl-inline-link" data-testid={testId}>{email}</a>
);

// Only rendered when the backend config says intake is open and names a usable https privacy page
// (see KennismakingSection). A 503 on submit hands control back through onUnavailable.
const DemoRequestForm = ({ privacyUrl, onUnavailable }) => {
  const [phase, setPhase] = useState('idle'); // idle | pending | success | error
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [honeypot, setHoneypot] = useState('');
  const attempt = useRef({ key: null, signature: null });
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const successRef = useRef(null);
  const contactEmail = getContactEmail();

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (phase === 'success') successRef.current?.focus();
  }, [phase]);

  const update = (field) => (event) => {
    setValues((current) => ({ ...current, [field]: event.target.value }));
    if (errors[field]) setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (inFlight.current || !privacyUrl) return;
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length) {
      const first = ['name', 'organization_name', 'email'].find((field) => found[field]);
      document.getElementById(`demo-${first}`)?.focus();
      return;
    }
    // One key per deliberate submission: an unchanged retry reuses it (the server answers idempotently),
    // changed content gets a new one.
    const current = signature(values);
    if (attempt.current.signature !== current) attempt.current = { key: newIdempotencyKey(), signature: current };

    inFlight.current = true;
    setPhase('pending');
    const payload = {
      name: values.name.trim(),
      organization_name: values.organization_name.trim(),
      email: values.email.trim(),
      message: values.message.trim() || null,
      company_website: honeypot || null,
    };
    const outcome = await submitDemoRequest(payload, attempt.current.key);
    inFlight.current = false;
    if (!mounted.current) return;
    if (outcome.kind === 'received') {
      setValues(EMPTY);
      setErrors({});
      attempt.current = { key: null, signature: null };
      setPhase('success');
    } else if (outcome.kind === 'unavailable') {
      setPhase('idle');
      onUnavailable?.();
    } else if (outcome.kind === 'invalid') {
      const mapped = {};
      outcome.fields.forEach((field) => {
        if (DEMO.invalid[field]) mapped[field] = DEMO.invalid[field];
      });
      setErrors(mapped);
      setPhase(Object.keys(mapped).length ? 'idle' : 'error');
    } else {
      if (outcome.kind === 'conflict') attempt.current = { key: null, signature: null };
      setPhase('error');
    }
  };

  if (phase === 'success') {
    return (
      <div className="zl-card" data-testid="demo-card">
        <div ref={successRef} tabIndex={-1} role="status" className="zl-success" data-testid="demo-success">
          <p>{DEMO.success}</p>
        </div>
      </div>
    );
  }

  const pending = phase === 'pending';
  const describe = (id, hasHelp) => [hasHelp ? `${id}-help` : null, errors[id.replace('demo-', '')] ? `${id}-error` : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="zl-card" data-testid="demo-card">
      <h3>{DEMO.title}</h3>
      <form onSubmit={handleSubmit} noValidate aria-busy={pending} data-testid="demo-form">
        <Field id="demo-name" label={`${DEMO.fields.name} *`} error={errors.name}>
          <input
            id="demo-name" name="name" type="text" autoComplete="name" required maxLength={LIMITS.name}
            value={values.name} onChange={update('name')} disabled={pending}
            aria-invalid={errors.name ? 'true' : undefined} aria-describedby={describe('demo-name', false)}
          />
        </Field>
        <Field id="demo-organization_name" label={`${DEMO.fields.organization_name} *`} error={errors.organization_name}>
          <input
            id="demo-organization_name" name="organization_name" type="text" autoComplete="organization" required maxLength={LIMITS.organization_name}
            value={values.organization_name} onChange={update('organization_name')} disabled={pending}
            aria-invalid={errors.organization_name ? 'true' : undefined} aria-describedby={describe('demo-organization_name', false)}
          />
        </Field>
        <Field id="demo-email" label={`${DEMO.fields.email} *`} error={errors.email}>
          <input
            id="demo-email" name="email" type="email" autoComplete="email" inputMode="email" required maxLength={LIMITS.email}
            value={values.email} onChange={update('email')} disabled={pending}
            aria-invalid={errors.email ? 'true' : undefined} aria-describedby={describe('demo-email', false)}
          />
        </Field>
        <Field id="demo-message" label={DEMO.fields.message} error={errors.message} help={DEMO.help}>
          <textarea
            id="demo-message" name="message" rows={4} maxLength={LIMITS.message}
            value={values.message} onChange={update('message')} disabled={pending}
            aria-invalid={errors.message ? 'true' : undefined} aria-describedby={describe('demo-message', true)}
          />
        </Field>

        {/* Honeypot: invisible to people and assistive technology; bots that fill it are dropped server-side. */}
        <div className="zl-honeypot" aria-hidden="true">
          <label htmlFor="demo-company_website">Laat dit veld leeg</label>
          <input id="demo-company_website" name="company_website" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
        </div>

        <p className="zl-small zl-privacy" data-testid="demo-privacy">
          {DEMO.privacyLead}{' '}
          <a href={privacyUrl} target="_blank" rel="noopener noreferrer">{DEMO.privacyLink}</a>.
        </p>

        <button type="submit" disabled={pending} className="zl-btn zl-btn-primary" data-testid="demo-submit">
          {pending ? DEMO.pending : DEMO.submit}
        </button>

        <p className="zl-status" role="status" aria-live="polite">{pending ? DEMO.pending : ''}</p>
        {phase === 'error' && (
          <div role="alert" className="zl-form-error" data-testid="demo-error">
            <p>{contactEmail ? <>{DEMO.errorWithContact} <ContactLink email={contactEmail} />.</> : DEMO.error}</p>
          </div>
        )}
        {contactEmail && <p className="zl-small">{DEMO.preferMail} <ContactLink email={contactEmail} testId="demo-prefer-mail" /></p>}
      </form>
    </div>
  );
};

export default DemoRequestForm;
